import React, { useEffect, useState } from 'react';
import { Json, JsonObjectArray } from '../../store/appState';
import styled from 'styled-components/macro';
import { DetailsList, DetailsListLayoutMode, Selection, SelectionMode, IColumn, IDetailsHeaderProps } from '@fluentui/react/lib/DetailsList';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { IRenderFunction } from '@fluentui/react/lib/Utilities';
import { Sticky } from '@fluentui/react/lib/Sticky';
import { TextField } from '@fluentui/react/lib/TextField';
import { Announced } from '@fluentui/react/lib/Announced';
import Flex from './Flex';
import { DefaultButton } from '@fluentui/react';

export interface JsonTableProps {
    data: JsonObjectArray;
    scrollable: boolean;
    onEdit?: (item: Json) => void;
}

const classNames = mergeStyleSets({
    columns: {
        padding: 0,
        fontSize: '1.2rem',
    },
});

const controlStyles = {
    root: {
        margin: '0 10px 20px 0',
        maxWidth: '300px',
    },
};

/** Shows JSON as a nice data table */
export default function JsonTable(props: JsonTableProps): JSX.Element {
    const columns: IColumn[] = props.data.length === 0 ? [] : Object.keys(props.data[0]).map((item: string, index: number) =>
    ({
        key: item,
        name: item,
        fieldName: item,
        minWidth: index === 0 ? 40 : 100,
        maxWidth: index === 0 ? 100 : (item === 'json' ? 650 :  250),
        isRowHeader: true,
        isResizable: true,
        isSorted: false,
        isSortedDescending: false,
        sortAscendingAriaLabel: 'Sorted A to Z',
        sortDescendingAriaLabel: 'Sorted Z to A',
        // onColumnClick: this._onColumnClick,
        isMultiline: true,
        data: 'string',
        className: classNames.columns,
    }));

    if (props.onEdit) {
        columns.push({
            key: "edit",
            name: "Edit",
            minWidth: 20,
            maxWidth: 150,
            isRowHeader: true,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false,
            sortAscendingAriaLabel: 'Sorted A to Z',
            sortDescendingAriaLabel: 'Sorted Z to A',
            onRender: (item) => (
                <DefaultButton onClick={() => { props.onEdit(item); }}>Edit</DefaultButton>
            ),
            isMultiline: false,
            className: classNames.columns,
        });
    }

    const [filterText, setFilterText] = useState("");
    const items = filterText === "" ? props.data : props.data.filter(
        (row: Json) => {
            for (const val of Object.values(row)) {
                if (val && val.toString().toUpperCase().indexOf(filterText) >= 0) {
                    return true;
                }
            }
            return false;
        }
    );

    const _onChangeText = (ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, text: string): void => {
        setFilterText(text.toUpperCase());
    };

    const table = <DetailsList
        items={items}
        compact={false}
        columns={columns}
        selectionMode={SelectionMode.none}
        getKey={space => space.id}
        setKey="none"
        layoutMode={DetailsListLayoutMode.justified}
        isHeaderVisible={true}
        onRenderDetailsHeader={
            // tslint:disable-next-line:jsx-no-lambda
            (detailsHeaderProps: IDetailsHeaderProps, defaultRender: IRenderFunction<IDetailsHeaderProps>) => (
                <Sticky>
                    {defaultRender(detailsHeaderProps)}
                </Sticky>
            )}
    />;
    return (
        <Wrapper>
            <Flex alignItems='baseline'>
                <TextField placeholder='Search' onChange={_onChangeText} styles={controlStyles} />
                <Announced message={`Number of items after filter applied: ${items.length}.`} />
                <div>{items.length} results</div>
            </Flex>

            <PaneWrapper css={props.scrollable ? "" : "height: auto"}>
                {props.scrollable && <ScrollablePane>
                    {table}
                </ScrollablePane>}
                {!props.scrollable && <>
                    {table}
                </>}
            </PaneWrapper>
        </Wrapper >
    );
}

const Wrapper = styled.div`
`;
const PaneWrapper = styled.div`
    font-size: 1.2rem;
    height: 40vh;
    position: relative;
`;

const colHeader = styled.div`
    font-size: 1.2rem;
`;
